import { MARKETING_RUBRIKEN, type Me, type VisibleArea } from '@portal/contracts';
import { ContactRound, Crown, KeyRound, LayoutDashboard, LayoutGrid, LogOut, Megaphone, Menu, Moon, Package, Settings, Store, Sun, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { onLinkClick } from '../router.js';
import type { Theme } from '../theme.js';

interface Props {
  me: Me;
  areas: VisibleArea[];
  activeArea: string | null;
  theme: Theme;
  onToggleTheme(): void;
}

const areaIcons = { arbeitsplatz: LayoutDashboard, kunden: Store, crm: ContactRound, loyalty: Crown, lager: Package, marketing: Megaphone, system: Settings };
const passwordUrl = 'https://login.parfuemerie-brueckner.com/if/flow/brueckner-passwort-aendern/';

/** Servergefilterte Navigation; Desktop-Iconleiste, mobil beschriftete Aufklappnavigation. */
export function Header({ me, areas, activeArea, theme, onToggleTheme }: Props) {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [labelsDismissed, setLabelsDismissed] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const profile = useRef<HTMLDivElement>(null);
  const profileButton = useRef<HTMLButtonElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const initials = me.displayName.trim().split(/\s+/).filter(Boolean).map((part) => Array.from(part)[0]).filter(Boolean);
  const monogram = (initials.length > 1 ? initials[0]! + initials[initials.length - 1]! : initials[0] ?? '?').toLocaleUpperCase('de');

  useEffect(() => {
    if (!profileOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !profile.current?.contains(event.target)) setProfileOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [profileOpen]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setLabelsDismissed(true);
      if (profileOpen) { setProfileOpen(false); profileButton.current?.focus(); }
      else if (open) { setOpen(false); menuButton.current?.focus(); }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open, profileOpen]);

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(false);
    try { await api.logout(); }
    catch { setLogoutError(true); setLoggingOut(false); }
  };

  return (
    <div className="sh-rail">
      <header className="sh-head">
        <a href="/" className="sh-brand" onClick={onLinkClick('/')} aria-label="Arbeitsplatz">
          <img className="sh-crest" src="/wappen/wappen-original.png" alt="Parfümerie Brückner" width={28} height={36} />
        </a>
        <button ref={menuButton} type="button" className="sh-menu pb-button pb-button--quiet" aria-expanded={open} aria-controls="sh-nav" onClick={() => { setOpen(!open); setProfileOpen(false); }}>
          {open ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />} Menü
        </button>

      </header>
      <nav id="sh-nav" className={`sh-nav${open ? ' is-open' : ''}${labelsDismissed ? ' labels-dismissed' : ''}`} aria-label="Hauptnavigation">
        {areas.map((a) => {
          const Icon = areaIcons[a.key as keyof typeof areaIcons] ?? LayoutGrid;
          const link = (
            <a key={a.key} href={a.path} className={`sh-nav__item${a.key === activeArea ? ' is-active' : ''}`} aria-label={a.label} aria-current={a.key === activeArea ? 'page' : undefined}
              onMouseEnter={() => setLabelsDismissed(false)} onFocus={() => setLabelsDismissed(false)}
              onClick={(e) => {
                setOpen(false);
                onLinkClick(a.path)(e);
                if (e.defaultPrevented && window.matchMedia('(max-width: 650px)').matches) {
                  requestAnimationFrame(() => document.getElementById('sh-content')?.focus());
                }
              }}>
              <Icon size={22} strokeWidth={1.7} aria-hidden />
              <span className="sh-nav__label" aria-hidden>{a.label}</span>
            </a>
          );
          // #870: Marketing aufklappbar mit den Rubriken Übersicht · Veröffentlichung · Produktion (mobile Menü; Desktop: Bereichsspalte)
          const rubriken = a.key === 'marketing' && a.modules.some((m) => m.key === 'marketing') && (
            <ul key={`${a.key}-rubriken`} className="sh-nav__rubriken" aria-label="Marketing-Rubriken">
              {MARKETING_RUBRIKEN.map((r) => (
                <li key={r.key}>
                  <a href={r.path} onClick={(e) => { setOpen(false); onLinkClick(r.path)(e); }}>{r.label}</a>
                </li>
              ))}
            </ul>
          );
          return rubriken ? [link, rubriken] : link;
        })}
      </nav>
        <div className="sh-tools">
          <button type="button" className="sh-theme" role="switch" aria-checked={theme === 'dark'} aria-label="Dunkelmodus" onClick={onToggleTheme}>
            {theme === 'dark' ? <Sun size={21} aria-hidden /> : <Moon size={21} aria-hidden />}
          </button>
          <div ref={profile} className="sh-profile" onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setProfileOpen(false);
          }}>
            <button ref={profileButton} type="button" className="sh-avatar" aria-label={`Benutzerkonto: ${me.displayName}`} aria-expanded={profileOpen} aria-controls="sh-account" onClick={() => { setProfileOpen(!profileOpen); setOpen(false); }}>
              <span aria-hidden>{monogram}</span>
            </button>
            {profileOpen && (
              <div id="sh-account" className="sh-account" role="region" aria-label="Benutzerkonto">
                <p className="sh-account__name">{me.displayName}</p>
                <a href={passwordUrl}><KeyRound size={18} aria-hidden /> Passwort ändern</a>
                <button type="button" aria-disabled={loggingOut} onClick={() => void logout()}><LogOut size={18} aria-hidden /> {loggingOut ? 'Wird ausgeloggt …' : 'Ausloggen'}</button>
                {logoutError && <p className="sh-account__error" role="alert">Ausloggen nicht möglich. Bitte erneut versuchen.</p>}
              </div>
            )}
          </div>
        </div>
    </div>
  );
}

export function SubNav({ area, activeModule }: { area: VisibleArea; activeModule: string | null }) {
  return (
    <nav className="sh-sub" aria-label={`Bereich ${area.label}`}>
      {area.modules.map((m) => (
        <a key={m.key} href={m.path} onClick={onLinkClick(m.path)} className={m.key === activeModule ? 'is-active' : ''} aria-current={m.key === activeModule ? 'page' : undefined}>
          {m.label}
        </a>
      ))}
      {area.key === 'arbeitsplatz' && <span className="sh-sub__disabled" aria-disabled="true" title="Noch nicht verfügbar">Vorgänge</span>}
    </nav>
  );
}
